import { useParams, Link } from 'react-router-dom';
import { articleDetails, allArticles } from '@/mocks/articleData';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleDetails[slug] : null;
  const meta = allArticles.find((a) => a.slug === slug);

  if (!article || !meta) {
    return (
      <div className="min-h-screen bg-background-50 pt-28 pb-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
          <Link to="/articles" className="text-primary-600 hover:text-primary-700 no-underline">返回文章列表</Link>
        </div>
      </div>
    );
  }

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://echo64.cn/' },
          { '@type': 'ListItem', position: 2, name: '文章中心', item: 'https://echo64.cn/articles' },
          { '@type': 'ListItem', position: 3, name: article.title, item: `https://echo64.cn/article/${slug}` },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        datePublished: article.date,
        author: { '@type': 'Organization', name: '喑网91' },
        publisher: { '@type': 'Organization', name: '喑网91' },
        url: `https://echo64.cn/article/${slug}`,
      },
      {
        '@type': 'WebPage',
        name: `${article.title} - 喑网91`,
        url: `https://echo64.cn/article/${slug}`,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <article className="min-h-screen bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 pt-28 pb-16">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors no-underline whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/articles" className="hover:text-primary-600 transition-colors no-underline whitespace-nowrap">文章中心</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium truncate">{article.title}</span>
          </nav>

          {/* Header */}
          <header className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs text-primary-600 bg-primary-100 px-2.5 py-1 rounded-full font-medium">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">{article.date}</span>
            </div>
            <h1
              className="text-2xl md:text-3xl font-bold text-foreground-950 leading-tight mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              {article.title}
            </h1>
          </header>

          {/* Content */}
          <div className="prose-custom space-y-6">
            {article.content.map((paragraph, idx) => (
              <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>

          {/* Related Articles */}
          {article.relatedArticles.length > 0 && (
            <section className="mt-14 pt-10 border-t border-background-200/70">
              <h3
                className="text-lg font-semibold text-foreground-950 mb-5"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {article.relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="p-4 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-200 no-underline"
                  >
                    <span className="text-sm text-foreground-800 hover:text-primary-600 transition-colors font-medium">
                      {rel.title}
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Back to articles */}
          <div className="mt-10">
            <Link
              to="/articles"
              className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors no-underline font-medium"
            >
              <i className="ri-arrow-left-line"></i>
              返回文章列表
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}